import React, { useEffect, useMemo, useRef, useState } from "react";
import { readCache, writeCache } from "../dataCache";
import MobileScrollArea from "../components/MobileScrollArea";

const API_URL = window.location.origin;

const customFields = [
  { label: "Email", value: "email" },
  { label: "Name", value: "name" },
  { label: "Company", value: "company" },
  { label: "First Name", value: "FirstName" },
  { label: "Last Name", value: "LastName" }
];

function getId(item) {
  return String(item?._id || item?.id || item?.name || "");
}

function formatDate(date) {
  if (!date) return "-";
  return new Date(date).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function listContactsCount(list, contacts = []) {
  const stored = Array.isArray(list.contacts) ? list.contacts.length : 0;
  const byName = contacts.filter((c) => c.list === list.name || c.listId === getId(list)).length;
  return Math.max(stored, byName);
}

export default function ListsPage() {
  const [lists, setLists] = useState(() => readCache("lists", []));
  const [contacts, setContacts] = useState(() => readCache("contacts", []));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editList, setEditList] = useState(null);
  const [activeTab, setActiveTab] = useState("All");
  const [search, setSearch] = useState("");
  const [menuOpen, setMenuOpen] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [contactMenuOpen, setContactMenuOpen] = useState(null);
  const [infoContact, setInfoContact] = useState(null);
  const [favoriteIds, setFavoriteIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sendx_list_favorites") || "[]");
    } catch {
      return [];
    }
  });
  const [pageNo, setPageNo] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [detailList, setDetailList] = useState(null);

  async function loadAll() {
    try {
      const [listRes, contactRes] = await Promise.all([
        fetch(`${API_URL}/api/lists`),
        fetch(`${API_URL}/api/contacts`)
      ]);
      const listData = listRes.ok ? await listRes.json() : [];
      const contactData = contactRes.ok ? await contactRes.json() : [];
      const nextLists = Array.isArray(listData) ? listData : [];
      const nextContacts = Array.isArray(contactData) ? contactData : [];
      setLists(nextLists);
      setContacts(nextContacts);
      writeCache("lists", nextLists);
      writeCache("contacts", nextContacts);
    } catch (error) {
      console.log(error.message);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    localStorage.setItem("sendx_list_favorites", JSON.stringify(favoriteIds));
  }, [favoriteIds]);

  const filteredLists = useMemo(() => {
    const q = search.toLowerCase().trim();
    return lists.filter((list) => {
      const id = getId(list);
      const matchesTab = activeTab === "Favorites" ? favoriteIds.includes(id) : true;
      const matchesSearch = !q ||
        String(list.name || "").toLowerCase().includes(q) ||
        String(list.listType || "Single Opt-in").toLowerCase().includes(q);
      return matchesTab && matchesSearch && list.archived !== true;
    });
  }, [lists, search, activeTab, favoriteIds]);

  const totalPages = Math.max(1, Math.ceil(filteredLists.length / perPage));
  const pagedLists = filteredLists.slice((pageNo - 1) * perPage, pageNo * perPage);

  useEffect(() => {
    setPageNo(1);
    setSelectedIds([]);
  }, [search, activeTab, perPage]);

  function toggleFavorite(list) {
    const id = getId(list);
    setFavoriteIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  function toggleSelected(id) {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  function toggleAllSelected() {
    const ids = pagedLists.map(getId);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    setSelectedIds((prev) => allSelected ? prev.filter((id) => !ids.includes(id)) : Array.from(new Set([...prev, ...ids])));
  }

  function openCreate() {
    setEditList(null);
    setDrawerOpen(true);
  }

  function openEdit(list) {
    setEditList(list);
    setDrawerOpen(true);
    setMenuOpen(null);
  }

  async function deleteList(list) {
    if (!window.confirm(`Delete "${list.name}"?`)) return;
    await fetch(`${API_URL}/api/lists/${getId(list)}`, { method: "DELETE" });
    setMenuOpen(null);
    await loadAll();
  }

  async function deleteSelectedLists() {
    if (!selectedIds.length) return;
    if (!window.confirm("Delete selected lists?")) return;
    for (const id of selectedIds) {
      await fetch(`${API_URL}/api/lists/${id}`, { method: "DELETE" });
    }
    setSelectedIds([]);
    await loadAll();
  }

  function exportList(list) {
    const rows = contacts.filter((c) => c.list === list.name || c.listId === getId(list));
    const csv = [
      "Name,Email,Company,List,Tag",
      ...rows.map((c) => [
        c.name || `${c.firstName || ""} ${c.lastName || ""}`.trim(),
        c.email || "",
        c.company || "",
        list.name || "",
        c.tag || ""
      ].map((v) => `"${String(v).replaceAll('"', '""')}"`).join(","))
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${list.name || "list"}-contacts.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setMenuOpen(null);
  }

  function archiveList(list) {
    alert("Archive option UI added. Backend archive endpoint is not available yet, so this list is not permanently archived.");
    setMenuOpen(null);
  }

  if (detailList) {
    return (
      <>
      <ListDetailPage
        list={detailList}
        contacts={contacts.filter((c) => c.list === detailList.name || c.listId === getId(detailList))}
        onBack={() => setDetailList(null)}
        onEdit={() => openEdit(detailList)}
        onRefresh={loadAll}
      />
      {drawerOpen && <CreateListDrawer editList={editList} onClose={() => { setDrawerOpen(false); setEditList(null); }} onCreated={() => { setDrawerOpen(false); setEditList(null); loadAll(); }} />}
      </>
    );
  }

  return (
    <section className="sx-lists-page sx-ref-lists-page">
      <div className="sx-lists-header sx-ref-list-header">
        <h1>Lists</h1>
      </div>

      <div className="sx-list-nav-row">
        <div className="sx-list-tabs sx-ref-tabs">
          <button className={activeTab === "All" ? "active" : ""} onClick={() => setActiveTab("All")}>All</button>
          <button className={activeTab === "Favorites" ? "active" : ""} onClick={() => setActiveTab("Favorites")}>Favorites</button>
        </div>
        <button className="sendx-yellow-btn sx-list-create-btn" onClick={openCreate}>+ Create List</button>
      </div>

      <div className="sx-ref-list-card">
        {selectedIds.length > 0 ? (
          <div className="sx-ref-list-bulkbar">
            <button className="sx-danger-btn" onClick={deleteSelectedLists}>Delete Selected Lists</button>
            <button className="sx-ref-filter-btn">☷</button>
          </div>
        ) : (
          <div className="sx-ref-list-toolbar">
            <p>All {filteredLists.length} lists</p>
            <div className="sx-ref-search-wrap">
              <span>⌕</span>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search lists..." />
            </div>
            <button className="sx-ref-filter-btn">☷</button>
          </div>
        )}

        <MobileScrollArea className="sx-ref-table-wrap" ariaLabel="lists table">
          <table className="sx-ref-list-table">
            <thead>
              <tr>
                <th className="sx-check-col"><input className="sx-ref-checkbox" type="checkbox" checked={pagedLists.length > 0 && pagedLists.every((l) => selectedIds.includes(getId(l)))} onChange={toggleAllSelected} /></th>
                <th className="sx-star-col"></th>
                <th>Name <span>↕</span></th>
                <th>Opt In Type <span>↕</span></th>
                <th>Size <span>↕</span></th>
                <th>Date Created <span>↕</span></th>
                <th>Date Updated <span>↕</span></th>
                <th className="sx-actions-col"></th>
              </tr>
            </thead>
            <tbody>
              {pagedLists.map((list) => {
                const id = getId(list);
                return (
                  <tr key={id} onClick={() => setDetailList(list)}>
                    <td className="sx-check-col" onClick={(e) => e.stopPropagation()}><input className="sx-ref-checkbox" type="checkbox" checked={selectedIds.includes(id)} onChange={() => toggleSelected(id)} /></td>
                    <td className="sx-star-col" onClick={(e) => { e.stopPropagation(); toggleFavorite(list); }}><button className={favoriteIds.includes(id) ? "sx-star active" : "sx-star"}>★</button></td>
                    <td className="sx-list-name-cell">{list.name}</td>
                    <td>{list.listType || "Single Opt-in"}</td>
                    <td>{listContactsCount(list, contacts)}</td>
                    <td><div className="sx-date-cell"><span className="sx-ref-user-icon"></span><span>{formatDate(list.createdAt)}</span></div></td>
                    <td><div className="sx-date-cell"><span className="sx-ref-user-icon"></span><span>{formatDate(list.updatedAt || list.createdAt)}</span></div></td>
                    <td className="sx-actions-col" onClick={(e) => e.stopPropagation()}>
                      <button className="sx-list-dots" onClick={() => setMenuOpen(menuOpen === id ? null : id)}>⋮</button>
                      {menuOpen === id && (
                        <div className="sx-list-menu">
                          <button onClick={() => { setDetailList(list); setMenuOpen(null); }}>Info</button>
                          <button onClick={() => { setDetailList(list); setTimeout(() => window.dispatchEvent(new CustomEvent("open-list-import")), 60); setMenuOpen(null); }}>Import</button>
                          <button onClick={() => openEdit(list)}>Edit</button>
                          <button onClick={() => exportList(list)}>Export</button>
                          <button onClick={() => archiveList(list)}>Archive</button>
                          <button className="danger" onClick={() => deleteList(list)}>Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {pagedLists.length === 0 && (
                <tr><td className="sx-empty-row" colSpan="8">No lists found.</td></tr>
              )}
            </tbody>
          </table>
        </MobileScrollArea>

        <div className="sx-ref-footer">
          <span>Showing {pagedLists.length ? (pageNo - 1) * perPage + 1 : 0} to {Math.min(pageNo * perPage, filteredLists.length)} of {filteredLists.length} results</span>
          <div>
            <span>Items per page</span>
            <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option></select>
            <button disabled={pageNo <= 1} onClick={() => setPageNo(pageNo - 1)}>←</button>
            <button disabled={pageNo >= totalPages} onClick={() => setPageNo(pageNo + 1)}>→</button>
          </div>
        </div>
      </div>

      {drawerOpen && (
        <CreateListDrawer
          editList={editList}
          onClose={() => { setDrawerOpen(false); setEditList(null); }}
          onCreated={() => { setDrawerOpen(false); setEditList(null); loadAll(); }}
        />
      )}
    </section>
  );
}

function ListDetailPage({ list, contacts, onBack, onEdit, onRefresh }) {
  const [tab, setTab] = useState("Overview");
  const [search, setSearch] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [contactMenuOpen, setContactMenuOpen] = useState(null);
  const [infoContact, setInfoContact] = useState(null);

  useEffect(() => {
    const handler = () => setImportOpen(true);
    window.addEventListener("open-list-import", handler);
    return () => window.removeEventListener("open-list-import", handler);
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return contacts.filter((c) =>
      String(c.name || `${c.firstName || ""} ${c.lastName || ""}`).toLowerCase().includes(q) ||
      String(c.email || "").toLowerCase().includes(q)
    );
  }, [contacts, search]);

  function toggleAll() {
    const ids = filtered.map(getId);
    const all = ids.length && ids.every((id) => selectedIds.includes(id));
    setSelectedIds(all ? [] : ids);
  }

  function toggleOne(id) {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  async function deleteSelectedContacts() {
    if (!selectedIds.length) return;
    if (!window.confirm("Delete selected contacts?")) return;
    for (const id of selectedIds) await fetch(`${API_URL}/api/contacts/${id}`, { method: "DELETE" });
    setSelectedIds([]);
    onRefresh();
  }

  async function deleteOneContact(contact) {
    if (!window.confirm("Delete this contact?")) return;
    await fetch(`${API_URL}/api/contacts/${getId(contact)}`, { method: "DELETE" });
    setContactMenuOpen(null);
    setInfoContact(null);
    onRefresh();
  }

  async function unsubscribeContact(contact) {
    await fetch(`${API_URL}/api/contacts/${getId(contact)}/unsubscribe`, { method: "PATCH" });
    setContactMenuOpen(null);
    setInfoContact(null);
    onRefresh();
  }

  if (infoContact) {
    return (
      <ContactDetailView
        contact={infoContact}
        list={list}
        onBack={() => setInfoContact(null)}
        onUnsubscribe={() => unsubscribeContact(infoContact)}
        onDelete={() => deleteOneContact(infoContact)}
        onRefresh={onRefresh}
      />
    );
  }

  return (
    <section className="sx-list-detail-page">
      <div className="sx-detail-head">
        <div className="sx-breadcrumb"><button onClick={onBack}>‹ Lists</button><span>/</span><b>{list.name}</b><em>{contacts.length} contacts</em></div>
        <div className="sx-detail-actions"><button className="sendx-dark-btn" onClick={() => setManualOpen(true)}>+ Create contact</button><button className="sendx-yellow-btn" onClick={onEdit}>Edit List</button></div>
      </div>
      <div className="sx-list-tabs sx-ref-tabs"><button className={tab === "Overview" ? "active" : ""} onClick={() => setTab("Overview")}>Overview</button><button className={tab === "Analytics" ? "active" : ""} onClick={() => setTab("Analytics")}>Analytics</button></div>
      {tab === "Analytics" ? <ListAnalytics contacts={contacts} /> : (
        <>
          <div className="sx-detail-toolbar">
            {selectedIds.length ? <button className="sx-danger-btn" onClick={deleteSelectedContacts}>Delete Selected Contacts</button> : <p>All {filtered.length} contacts</p>}
            <div className="sx-ref-search-wrap"><span>⌕</span><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search contacts..." /></div>
            <button className="sendx-dark-btn" onClick={() => setImportOpen(true)}>⇩ Import contacts</button><button className="sx-ref-filter-btn">☷</button>
          </div>
          <div className="sx-detail-contact-card">
            {filtered.length === 0 ? <div className="sx-no-contacts"><h2>No contacts found</h2><p>Once contacts are added, they will appear here.</p></div> : (
              <table className="sx-detail-contact-table"><thead><tr><th><input className="sx-ref-checkbox" type="checkbox" checked={filtered.length > 0 && filtered.every((c) => selectedIds.includes(getId(c)))} onChange={toggleAll}/></th><th>Name</th><th>Email</th><th>Date Created</th><th>Date Updated</th><th></th></tr></thead><tbody>{filtered.map((c, index) => { const id = getId(c) || `${c.email}-${index}`; return <tr key={`${id}-${index}`} className="sx-contact-hover-row" onClick={() => setInfoContact(c)}><td onClick={(e) => e.stopPropagation()}><input className="sx-ref-checkbox" type="checkbox" checked={selectedIds.includes(id)} onChange={() => toggleOne(id)}/></td><td>{c.name || `${c.firstName || ""} ${c.lastName || ""}`.trim() || "-"}</td><td>{c.email}</td><td>{formatDate(c.createdAt)}</td><td>{formatDate(c.updatedAt || c.createdAt)}</td><td className="sx-contact-action-cell" onClick={(e) => e.stopPropagation()}><button className="sx-list-dots sx-contact-dots" onClick={() => setContactMenuOpen(contactMenuOpen === id ? null : id)}>⋮</button>{contactMenuOpen === id && <div className="sx-list-menu sx-contact-menu"><button onClick={() => { setInfoContact(c); setContactMenuOpen(null); }}>Info</button><button onClick={() => { setInfoContact(c); setContactMenuOpen(null); }}>Edit</button><button className="danger" onClick={() => deleteOneContact(c)}>Delete</button></div>}</td></tr>; })}</tbody></table>
            )}
          </div>
        </>
      )}
      {manualOpen && <ManualContactModal list={list} onClose={() => setManualOpen(false)} onDone={() => { setManualOpen(false); onRefresh(); }} />}
      {importOpen && <ImportContactsModal list={list} onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); onRefresh(); }} />}
    </section>
  );
}

function ManualContactModal({ list, contact, onClose, onDone }) {
  const isEdit = Boolean(contact);
  const [form, setForm] = useState({
    firstName: contact?.firstName || (contact?.name ? contact.name.split(" ")[0] : "") || "",
    lastName: contact?.lastName || (contact?.name ? contact.name.split(" ").slice(1).join(" ") : "") || "",
    email: contact?.email || "",
    company: contact?.company || ""
  });
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!form.email.trim()) return alert("Email required.");
    setSaving(true);
    const name = `${form.firstName} ${form.lastName}`.trim() || form.email.trim();
    const payload = { ...form, name, email: form.email.trim().toLowerCase() };
    if (!isEdit) { payload.list = list?.name; payload.listId = getId(list || {}); payload.status = "active"; }
    const url = isEdit ? `${API_URL}/api/contacts/${getId(contact)}` : `${API_URL}/api/contacts`;
    const method = isEdit ? "PATCH" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    const data = await res.json().catch(() => ({}));
    setSaving(false);
    if (!res.ok) return alert(data.message || "Contact save failed.");
    onDone(data && (data._id || data.id) ? data : { ...contact, ...payload });
  }
  return <div className="sx-drawer-overlay"><div className="sx-drawer-backdrop" onClick={onClose} /><aside className="sx-list-drawer sx-contact-drawer"><div className="sx-drawer-header"><h2>{isEdit ? "Edit Contact" : "Create Contact"}</h2><button onClick={onClose}>×</button></div><div className="sx-drawer-body sx-ref-modal"><label>First Name</label><input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })}/><label>Last Name</label><input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })}/><label>Email *</label><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}/><label>Company</label><input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}/></div><div className="sx-drawer-footer"><button className="sendx-dark-btn" onClick={onClose}>Cancel</button><button className="sendx-yellow-btn" disabled={saving} onClick={save}>{saving ? "Saving..." : (isEdit ? "Save" : "Create")}</button></div></aside></div>;
}

function ImportContactsModal({ list, onClose, onDone }) {
  const [mode, setMode] = useState("csv");
  const [step, setStep] = useState("select");
  const [text, setText] = useState("");
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  function parseRows(raw) {
    return raw
      .split(/\r?\n/)
      .map((x) => x.trim())
      .filter(Boolean)
      .map((line) => {
        const cols = line.includes(",") ? line.split(",") : line.split(/\s+/);
        const clean = cols.map((x) => x.replace(/^"|"$/g, "").trim()).filter(Boolean);
        const emailIndex = clean.findIndex((c) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(c));
        if (emailIndex === -1) return null;
        const email = clean[emailIndex].toLowerCase();
        const name = clean[emailIndex === 0 ? 1 : 0] || email.split("@")[0];
        return {
          name,
          email,
          firstName: name.split(" ")[0] || "",
          lastName: name.split(" ").slice(1).join(" ") || "",
          company: clean[2] || "",
          list: list.name,
          listId: getId(list),
          status: "active"
        };
      })
      .filter(Boolean);
  }

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setText(await file.text());
  }

  function continueToMatch() {
    const parsed = parseRows(text);
    if (!parsed.length) return alert("No valid emails found. Use CSV/text like: Name,email@example.com,Company");
    setRows(parsed);
    setStep("match");
  }

  async function importNow() {
    if (!rows.length) return;
    setSaving(true);
    const res = await fetch(`${API_URL}/api/contacts/bulk-import`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contacts: rows })
    });
    const data = await res.json().catch(() => ({}));
    setSaving(false);
    alert(data.message || `${rows.length} contacts imported.`);
    onDone();
  }

  return (
    <div className="sx-modal-overlay">
      <div className="sx-small-modal sx-import-modal sx-import-reference-modal">
        <h2>Import Contacts</h2>
        <p>CSV file ya manual paste dono supported hain.</p>

        {step === "select" ? (
          <>
            <div className="sx-list-tabs sx-ref-tabs sx-import-tabs">
              <button className={mode === "csv" ? "active" : ""} onClick={() => setMode("csv")}>CSV Import *</button>
              <button className={mode === "manual" ? "active" : ""} onClick={() => setMode("manual")}>Manual Import *</button>
            </div>

            {mode === "csv" && <input type="file" accept=".csv,.txt" onChange={handleFile} />}
            <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="John Doe,john@example.com,Company\nJane,jane@example.com,Company" />

            <div className="sx-modal-actions">
              <button onClick={onClose}>Cancel</button>
              <button className="sendx-yellow-btn" onClick={continueToMatch}>Continue</button>
            </div>
          </>
        ) : (
          <>
            <h3>Match fields</h3>
            <p>{rows.length} valid contacts found. These fields will be saved:</p>
            <div className="sx-match-preview">
              <b>Name</b><b>Email</b><b>Company</b>
              {rows.slice(0, 5).map((row, i) => (
                <React.Fragment key={i}>
                  <span>{row.name}</span><span>{row.email}</span><span>{row.company || "-"}</span>
                </React.Fragment>
              ))}
            </div>
            <div className="sx-modal-actions">
              <button onClick={() => setStep("select")}>Back</button>
              <button className="sendx-yellow-btn" disabled={saving} onClick={importNow}>{saving ? "Importing..." : "Import Contacts"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ContactDetailView({ contact, list, onBack, onUnsubscribe, onDelete, onRefresh }) {
  const [tab, setTab] = useState("Profile");
  const [editOpen, setEditOpen] = useState(false);
  const [data, setData] = useState(contact);
  useEffect(() => { setData(contact); }, [contact]);
  const name = data.name || `${data.firstName || ""} ${data.lastName || ""}`.trim() || data.email || "Contact";
  const created = formatDate(data.createdAt);
  return (
    <section className="sx-list-detail-page sx-contact-profile-page sx-ref-contact-profile">
      <div className="sx-contact-profile-top">
        <div className="sx-breadcrumb"><button onClick={onBack}>‹ Contacts</button><span>/</span><b>{name}</b></div>
        <div className="sx-detail-actions"><button className="sendx-yellow-btn" onClick={() => setEditOpen(true)}>Edit Contact</button><button className="sendx-dark-btn" onClick={onUnsubscribe}>Unsubscribe</button><button className="sx-danger-btn" onClick={onDelete}>Delete</button></div>
      </div>
      <div className="sx-list-tabs sx-ref-tabs"><button className={tab === "Profile" ? "active" : ""} onClick={() => setTab("Profile")}>Profile</button><button className={tab === "Timeline" ? "active" : ""} onClick={() => setTab("Timeline")}>Timeline</button></div>
      {tab === "Profile" ? (
        <div className="sx-contact-profile-grid">
          <div><h3>Personal information</h3><div className="sx-profile-card"><p><span>First name</span><b>{data.firstName || name.split(" ")[0] || "-"}</b></p><p><span>Last name</span><b>{data.lastName || name.split(" ").slice(1).join(" ") || "-"}</b></p><p><span>Email</span><b>{data.email || "-"}</b></p><p><span>Company</span><b>{data.company || "-"}</b></p><p><span>Status</span><b>{data.status || "active"}</b></p></div></div>
          <div><h3>Lists</h3><p>{data.list || "No lists found for this contact."}</p><h3>Tags</h3><p>{data.tag || "No tags found for this contact."}</p></div>
          <div><h3>Custom Fields</h3><p>No custom fields found for this contact.</p></div>
        </div>
      ) : (
        <div className="sx-contact-timeline"><h3>{created}</h3><div className="sx-timeline-card"><div><span className="sx-timeline-dot">✉</span><b>{name}</b> contact created manually <em>{created}</em></div>{data.list && <div><span className="sx-timeline-dot">☰</span><b>{name}</b> added to list <strong>{data.list}</strong></div>}</div></div>
      )}
      {editOpen && <ManualContactModal list={list} contact={data} onClose={() => setEditOpen(false)} onDone={(updated) => { setEditOpen(false); if (updated) setData(updated); onRefresh && onRefresh(); }} />}
    </section>
  );
}

function ListAnalytics({ contacts }) {
  return <div className="sx-list-analytics"><div className="stat-card"><p>Total contacts</p><h1>{contacts.length}</h1></div><div className="stat-card"><p>Active contacts</p><h1>{contacts.filter((c) => (c.status || "active") === "active").length}</h1></div><div className="stat-card"><p>Unsubscribed</p><h1>{contacts.filter((c) => c.status === "unsubscribed").length}</h1></div></div>;
}

function CreateListDrawer({ editList, onClose, onCreated }) {
  const [name, setName] = useState(editList?.name || "");
  const [thankYouEnabled, setThankYouEnabled] = useState(Boolean(editList?.thankYouEnabled));
  const [senders, setSenders] = useState([]);
  const [senderOpen, setSenderOpen] = useState(false);
  const [senderSearch, setSenderSearch] = useState("");
  const [selectedSender, setSelectedSender] = useState(null);
  const [addSenderOpen, setAddSenderOpen] = useState(false);
  const [senderForm, setSenderForm] = useState({ name: "", email: "" });
  const [subject, setSubject] = useState(editList?.subject || "{{FirstName}} Awesome to have you");
  const [fieldOpen, setFieldOpen] = useState(false);
  const [listType, setListType] = useState(editList?.listType || "Single Opt-in");
  const [testEmail, setTestEmail] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState("thankyou");
  const [thankYouHtml, setThankYouHtml] = useState(editList?.thankYouHtml || defaultThankYouHtml(editList?.name || ""));
  const [confirmHtml, setConfirmHtml] = useState(editList?.confirmHtml || defaultConfirmHtml(editList?.name || ""));
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSenders(); }, []);
  useEffect(() => { if (!editList) { setThankYouHtml(defaultThankYouHtml(name)); setConfirmHtml(defaultConfirmHtml(name)); } }, [name, editList]);

  async function loadSenders() {
    const res = await fetch(`${API_URL}/api/senders`).catch(() => null);
    const data = res?.ok ? await res.json() : [];
    setSenders(Array.isArray(data) ? data : []);
    const found = Array.isArray(data) ? data.find((s) => s.email === editList?.senderEmail) : null;
    setSelectedSender(found || (Array.isArray(data) && data[0]) || null);
  }

  const filteredSenders = useMemo(() => {
    const q = senderSearch.toLowerCase();
    return senders.filter((s) => String(s.name || "").toLowerCase().includes(q) || String(s.email || "").toLowerCase().includes(q));
  }, [senders, senderSearch]);

  function insertField(value) { setSubject((prev) => `${prev || ""} {{${value}}}`.trim()); setFieldOpen(false); }

  async function addSender() {
    if (!senderForm.name.trim() || !senderForm.email.trim()) return alert("Sender name and email are required.");
    const res = await fetch(`${API_URL}/api/senders`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(senderForm) });
    const data = await res.json();
    if (!res.ok) return alert(data.message || "Sender add failed.");
    setSenders((prev) => [data, ...prev]); setSelectedSender(data); setSenderForm({ name: "", email: "" }); setAddSenderOpen(false); setSenderOpen(false);
  }

  async function createList() {
    if (!name.trim()) return alert("List name required.");
    if (thankYouEnabled && !selectedSender?.email) return alert("Please select or add a sender.");
    setSaving(true);
    const payload = { name, thankYouEnabled, senderName: selectedSender?.name || "", senderEmail: selectedSender?.email || "", subject, listType, thankYouHtml, confirmHtml };
    const url = editList ? `${API_URL}/api/lists/${getId(editList)}` : `${API_URL}/api/lists`;
    const method = editList ? "PATCH" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    const list = await res.json().catch(() => ({}));
    setSaving(false);
    if (!res.ok) return alert(list.message || "List save failed. PATCH endpoint may be missing on backend.");
    if (thankYouEnabled && testEmail.trim() && !editList) await fetch(`${API_URL}/api/lists/${list._id}/subscribe`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ firstName: "Test", lastName: "User", name: "Test User", email: testEmail.trim(), company: "Demo" }) });
    onCreated();
  }

  if (editorOpen) return <TemplateEditor title={editingTemplate === "thankyou" ? "Edit Thank You Email" : "Edit Confirmation Email"} html={editingTemplate === "thankyou" ? thankYouHtml : confirmHtml} setHtml={editingTemplate === "thankyou" ? setThankYouHtml : setConfirmHtml} onClose={() => setEditorOpen(false)} />;

  return <div className="sx-drawer-overlay"><div className="sx-drawer-backdrop" onClick={onClose} /><aside className="sx-list-drawer"><div className="sx-drawer-header"><h2>{editList ? "Edit List" : "New List"}</h2><button onClick={onClose}>×</button></div><div className="sx-drawer-body"><label>List Name *</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter list name"/><div className="sx-checkbox-row"><input type="checkbox" checked={thankYouEnabled} onChange={(e) => setThankYouEnabled(e.target.checked)}/><span>Send automated thank you e-mail once user has subscribed to this list</span></div>{thankYouEnabled && <div className="sx-thankyou-card"><h3>Who is this from? *</h3><p>This will be displayed in the From field of your recipient's email client.</p><div className="sx-list-sender-dropdown"><button type="button" onClick={() => setSenderOpen(!senderOpen)}><span>{selectedSender ? `${selectedSender.name || "Sender"} <${selectedSender.email}>` : "Choose a sender"}</span><b>⌄</b></button>{senderOpen && <div className="sx-list-sender-menu"><input value={senderSearch} onChange={(e) => setSenderSearch(e.target.value)} placeholder="Search..." />{filteredSenders.length === 0 && <p className="sx-empty-small">No sender found.</p>}{filteredSenders.map((sender) => <button type="button" key={sender._id || sender.email} onClick={() => { setSelectedSender(sender); setSenderOpen(false); }}>{sender.name || "Sender"} &lt;{sender.email}&gt;</button>)}<button type="button" className="sx-add-sender-line" onClick={() => { setAddSenderOpen(true); setSenderOpen(false); }}>+ Add New Sender</button></div>}</div><label>Subject *</label><div className="sx-list-subject-row"><input value={subject} onChange={(e) => setSubject(e.target.value)} /><div className="sx-list-custom-field"><button type="button" onClick={() => setFieldOpen(!fieldOpen)}>Custom Fields <span>{fieldOpen ? "⌃" : "⌄"}</span></button>{fieldOpen && <div className="sx-list-custom-menu">{customFields.map((field) => <button type="button" key={field.value} onClick={() => insertField(field.value)}><span>{field.label}</span><em>Default</em></button>)}</div>}</div></div><EmailPreview html={thankYouHtml}/><button type="button" className="sendx-yellow-btn sx-edit-template-btn" onClick={() => { setEditingTemplate("thankyou"); setEditorOpen(true); }}>Edit</button>{listType === "Double Opt-in" && <><p className="sx-confirm-note">Confirm mail message must have ConfirmationUrl in content</p><EmailPreview html={confirmHtml}/><button type="button" className="sendx-yellow-btn sx-edit-template-btn" onClick={() => { setEditingTemplate("confirm"); setEditorOpen(true); }}>Edit</button></>}<label>Send test / subscribe email</label><input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="Enter subscriber email for testing"/></div>}<div className="sx-advanced-section"><h3>Advanced Setting</h3><label>List Type</label><select value={listType} onChange={(e) => setListType(e.target.value)}><option>Single Opt-in</option><option>Double Opt-in</option></select></div></div><div className="sx-drawer-footer"><button className="sendx-dark-btn" onClick={onClose}>Cancel</button><button className="sendx-yellow-btn" onClick={createList} disabled={saving}>{saving ? "Saving..." : editList ? "Save List" : "Create List"}</button></div>{addSenderOpen && <div className="sx-modal-overlay"><div className="sx-small-modal"><h2>Add New Sender</h2><p>This sender will be available in future lists and newsletters.</p><label>Sender name</label><input value={senderForm.name} onChange={(e) => setSenderForm({ ...senderForm, name: e.target.value })} placeholder="Sender Name"/><label>Sender email</label><input value={senderForm.email} onChange={(e) => setSenderForm({ ...senderForm, email: e.target.value })} placeholder="sender@example.com"/><div className="sx-modal-actions"><button onClick={() => setAddSenderOpen(false)}>Cancel</button><button className="sendx-yellow-btn" onClick={addSender}>Add Sender</button></div></div></div>}</aside></div>;
}

function EmailPreview({ html }) { return <div className="sx-email-preview"><iframe title="email-preview" srcDoc={html} /></div>; }

function TemplateEditor({ title, html, setHtml, onClose }) {
  const [tab, setTab] = useState("CONTENT");
  function addBlock(block) { const blocks = { TITLE: "<h2 style='font-family:Arial;text-align:center;'>New Title</h2>", PARAGRAPH: "<p style='font-family:Arial;line-height:1.6;'>New paragraph text goes here.</p>", LIST: "<ul><li>List item one</li><li>List item two</li></ul>", BUTTON: "<p style='text-align:center;'><a href='#' style='background:#111;color:#fff;padding:12px 22px;border-radius:6px;text-decoration:none;'>Click Here</a></p>", IMAGE: "<img src='https://via.placeholder.com/520x180' style='width:100%;border-radius:8px;' />", DIVIDER: "<hr style='border:0;border-top:1px solid #ddd;margin:25px 0;' />", SPACER: "<br/><br/>", HTML: "<div style='padding:15px;background:#f5f5f5;'>Custom HTML block</div>" }; setHtml(`${html}${blocks[block] || ""}`); }
  return <div className="sx-template-editor-overlay"><div className="sx-template-editor-top"><h2>{title}</h2><div><button className="sendx-dark-btn" onClick={onClose}>Send Test Email</button><button className="sendx-yellow-btn" onClick={onClose}>Save & Continue</button></div></div><div className="sx-template-editor-layout"><div className="sx-template-canvas"><iframe title="template-editor-preview" srcDoc={html} /></div><div className="sx-template-tools"><div className="sx-template-tool-tabs">{["CONTENT", "ROWS", "SETTINGS"].map((x) => <button key={x} className={tab === x ? "active" : ""} onClick={() => setTab(x)}>{x}</button>)}</div>{tab === "CONTENT" && <div className="sx-template-tool-grid">{["TITLE", "PARAGRAPH", "LIST", "IMAGE", "BUTTON", "TABLE", "DIVIDER", "SPACER", "SOCIAL", "HTML", "VIDEO", "ICONS", "MENU", "TEXT", "GIFS"].map((x) => <button key={x} onClick={() => addBlock(x)}>{x}</button>)}</div>}{tab === "ROWS" && <p className="sx-editor-muted">Rows layout options will appear here.</p>}{tab === "SETTINGS" && <p className="sx-editor-muted">Template settings will appear here.</p>}</div></div></div>;
}

function defaultThankYouHtml(name) { return `<div style="font-family:Arial;background:#f5f5f7;padding:40px;"><div style="max-width:460px;margin:auto;background:#fff;padding:45px;border-radius:8px;"><h2 style="text-align:center;color:#111;">Welcome to ${name || "Your List"}</h2><p>Hi {{FirstName}}, thanks for subscribing to ${name || "your list"}. We're glad to have you on board.</p><hr style="border:0;border-top:1px solid #ddd;margin:35px 0;"><small>Team ${name || "Your List"}</small></div></div>`; }
function defaultConfirmHtml(name) { return `<div style="font-family:Arial;background:#f5f5f7;padding:40px;"><div style="max-width:460px;margin:auto;background:#fff;padding:45px;border-radius:8px;text-align:center;"><h2>Confirm your subscription</h2><p>Hi {{FirstName}}, please confirm your subscription to ${name || "your list"} by clicking the button below.</p><a href="{{ConfirmationUrl}}" style="background:#111;color:#fff;padding:12px 22px;border-radius:6px;text-decoration:none;">Confirm subscription</a><p style="font-size:12px;color:#777;margin-top:25px;">If you did not request this, you can safely ignore this email.</p><hr style="border:0;border-top:1px solid #ddd;margin:35px 0;"><small>Team ${name || "Your List"}</small></div></div>`; }
